import styled from "@emotion/styled/macro";
import {
  tokens,
  Typography,
  Banner,
  BannerTypes,
  UtilityConnectionContainer,
  Modal,
  UtilityBillPastDueBalanceBanner,
} from "@sunrun/experience-ui-components";
import { useLocation, useParams } from "react-router-dom";
import qs from "qs";
import React, { useCallback, useMemo, useState } from "react";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import { useFlags } from "flagsmith/react";
import {
  HeaderButton,
  HeaderContentLayout,
} from "../templates/HeaderContentLayout";
import { ResponsiveCardLayout } from "../templates/CardLayouts";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import {
  ContactsUnavailableModal,
  isProspectNotQualifiedContactsError,
} from "components/organisms/ContactsUnavailableModal";
import { ProspectUnavailableModal } from "components/organisms/ProspectUnavailableModal";
import { isProspectUnavailable } from "utils/contactsErrors";
import { TitleTask } from "components/organisms/ConfirmInfo/TitleTask";
import { UtilityTask } from "components/organisms/ConfirmInfo/UtilityTask";
import { ConfirmInfoOptionalTask } from "components/molecules/ConfirmInfo/ConfirmInfoOptionalTask";
import { SiteModelTask } from "components/organisms/ConfirmInfo/SiteModelTask";
import { SiteModelModal } from "components/organisms/SiteModelModal";
import { UTILITY_LOAD_CALCULATOR_URL } from "constants/navigation";
import { useHqsEligibilityByOpportunityId, useHqsUrl } from "hooks/useHqs";
import { useAppSelector } from "store";
import { getPrimaryContact, getNamesOnTitle } from "utils/contacts";
import { useContactsData, useProposals, useUsageData } from "utils/swrHooks";
import { useNavigateToReview } from "utils/useNavigateHelper";
import { useUserProfile } from "cap/hooks/useCapSwr";
import { useUsageCalculatorUrl } from "hooks/useUsageCalculator";
import { QualifyCustomerModal } from "components/organisms/QualifyCustomerModal";
import { VivintOptionalTaskCard } from "components/organisms/ConfirmInfo/VivintOptionalTask";
import { UsageSlideout } from "components/organisms/UsageSlideout";
import { TitleInfoSlideout } from "components/organisms/TitleInfoSlideout";
// (TODO) Add this when we can put usage calculator into an iFrame
// import { UsageCalculatorSlideout } from "components/organisms/UsageCalculatorSlideout";
import { LOAD_CALCULATOR_UTILITIES } from "constants/utilityCompanies";
import { FeatureFlags } from "fixtures/features";
import { useGetUtilityConnectionUrl } from "hooks/useGetUtilityConnectionUrl";
import { useGetUtilityConnectionStatus } from "hooks/useGetUtilityConnectionStatus";
import { useContractReview } from "hooks/useContractReview";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";
import {
  is12MonthActualUsageMethodMessage,
  isRateScheduleMismatchMessage,
} from "utils/contractReviewUtils";
import { calculateUsageData } from "utils/usageUtils";
import { ConfirmHomeInfoTask } from "components/organisms/ConfirmInfo/ConfirmHomeInfoTask";
import { CheckoutCustomerInfoSlideout } from "checkout/components/organisms/CheckoutCustomerInfoSlideout";

const utilityAPICompanyList = [
  "APS",
  "ConEdison",
  "Eversource",
  "Fort Collins Utilities",
  "LADWP",
  "National Grid",
  "PG&E",
  "SCE",
  "SDG&E",
];

const ConfirmInfoPage: React.FC = () => {
  const navigateToReview = useNavigateToReview();
  const location = useLocation();
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [isQualifyModalOpen, setOpenQualifyModal] = useState(false);
  const { prospectId } = useParams();
  const [isSiteModelModalOpen, setOpenSiteModelModal] = useState(false);
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const { userProfile } = useUserProfile(authKey);

  const flags = useFlags([FeatureFlags.UTILITY_PAST_DUE_BALANCE_FLOW_ENABLED]);
  const isContractReviewEnabled =
    flags[FeatureFlags.CONTRACT_REVIEW]?.enabled ?? false;
  const utilityBillPastDueBalanceFlowIsEnabled =
    flags[FeatureFlags.UTILITY_PAST_DUE_BALANCE_FLOW_ENABLED]?.enabled ?? false;
  const { data: proposals } = useProposals(prospectId!, authKey);
  const activeProposalId = proposals?.[0]?.proposalId;
  const { messages: contractReviewMessages } = useContractReview(
    isContractReviewEnabled ? activeProposalId : undefined
  );

  const [showUsageSlideout, setShowUsageSlideout] = useState(
    queryParams.openUsageSlideout === "true"
  );
  const [showContractReviewBanners, setShowContractReviewBanners] =
    useState(false);
  const [showTitleSlideout, setShowTitleSlideout] = useState(false);
  const [showGetBillOnlineIframe, setShowGetBillOnlineIframe] = useState(false);
  const [showGetBillOnlineWarningModal, setShowGetBillOnlineWarningModal] =
    useState(false);
  const [showHomeInfoSlideout, setShowHomeInfoSlideout] = useState(false);
  const [isRefreshingHomeInfo, setIsRefreshingHomeInfo] = useState(false);
  const { getUrl, loading, url } = useGetUtilityConnectionUrl(authKey);

  // Get utility connection status
  const {
    status: intervalDataStatus,
    initialLoading: intervalDataInitialLoading,
    startPolling,
    stopPolling,
  } = useGetUtilityConnectionStatus(authKey, prospectId || null);

  const usageData = useUsageData(prospectId);
  const {
    prospect,
    attachments,
    error: usageError,
    isLoading: isProspectLoading,
    refetch: refetchProspect,
  } = usageData;

  const hasAdditionalSystem = prospect?.additionalSystem;

  const isUtilityAPICompany = useMemo(() => {
    return utilityAPICompanyList.includes(prospect?.utilityCompany || "");
  }, [prospect]);

  // Start polling when component mounts
  React.useEffect(() => {
    if (prospectId && isUtilityAPICompany) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [prospectId, startPolling, stopPolling, isUtilityAPICompany]);

  // (TODO) Add this when we can put usage calculator into an iFrame
  // const [showUsageCalculatorSlideout, setShowUsageCalculatorSlideout] =
  //   useState(false);
  const { contacts, contactsError, isContactsLoading } = useContactsData(
    prospectId as string,
    authKey
  );
  const usage = prospect;
  const usageCalculatorUrl = useUsageCalculatorUrl(prospectId, usage);

  const { utilityPastDueBalance, utilityCompany } = usage ?? {};
  const hasUploadedUtilityBill = attachments?.some(
    (attachment) => attachment.documentType === "Utility Bill"
  );
  const hasUploadedUtilityBillFileWithPastDueBalance = Boolean(
    hasUploadedUtilityBill &&
      utilityPastDueBalance != null &&
      utilityPastDueBalance > 0 &&
      utilityCompany
  );

  const { monthsEntered } = calculateUsageData({
    prospect: usage,
  });

  const onGetBillOnline = useCallback(
    (hasProposals: boolean) => {
      if (prospectId && !intervalDataStatus) {
        if (hasProposals || monthsEntered > 0) {
          setShowGetBillOnlineWarningModal(true);
        } else {
          getUrl(prospectId);
          setShowGetBillOnlineIframe(true);
        }
      }
    },
    [prospectId, intervalDataStatus, monthsEntered, getUrl]
  );

  const isSRM = userProfile && isSunrunManaged(userProfile);

  const isIllinois = useMemo(() => {
    return usage?.customerState === "IL";
  }, [usage]);

  const { data: isHqsEligible, error: hqsError } =
    useHqsEligibilityByOpportunityId(prospect?.opportunityId);
  const hqsUrl = useHqsUrl({ prospectId, earlyCapture: true });

  const handleContinue = useCallback(() => {
    navigateToReview();
  }, [navigateToReview]);

  const handleHomeInfoSaveSuccess = useCallback(async () => {
    setIsRefreshingHomeInfo(true);
    setShowHomeInfoSlideout(false);
    try {
      await refetchProspect();
    } finally {
      setIsRefreshingHomeInfo(false);
    }
  }, [refetchProspect]);

  const primaryContact = useMemo(() => getPrimaryContact(contacts), [contacts]);
  const isCreditCheckPassed = useMemo(
    () => primaryContact?.creditStatus.isPassed,
    [primaryContact]
  );
  const titleNames = useMemo(() => getNamesOnTitle(prospect), [prospect]);

  const buttons = useMemo(() => {
    return [
      {
        type: "primary",
        text: "Continue",
        dataTestId: "confirm-info--continue",
        onClick: handleContinue,
      } as HeaderButton,
    ];
  }, [handleContinue]);

  if (usageError) {
    return (
      <HeaderContentLayoutStyled
        content={
          <ErrorComponent
            context="ConfirmInfoPage"
            action="Get Usage"
            error={usageError}
          />
        }
        title="There was a Problem."
        description={`Something went wrong. Please try refreshing the page.`}
      />
    );
  }

  if (
    isProspectUnavailable({
      prospect,
      prospectLoading: isProspectLoading,
    })
  ) {
    return <ProspectUnavailableModal />;
  }

  if (contactsError && isProspectNotQualifiedContactsError(contactsError)) {
    return (
      <ContactsUnavailableModal
        prospect={prospect}
        userProfile={userProfile}
        errorMessage={contactsError?.response?.body?.message}
      />
    );
  }

  if (contactsError) {
    return (
      <HeaderContentLayoutStyled
        content={
          <ErrorComponent
            context="ConfirmInfoPage"
            action="Get Contacts"
            error={contactsError}
          />
        }
        title="There was a Problem."
        description={`Something went wrong. Please try refreshing the page.`}
      />
    );
  }

  return (
    <>
      <HeaderContentLayoutStyled
        smallPadding={true}
        title="Confirm Info & Tasks"
        description="Confirm information and complete the below tasks."
        buttons={buttons}
        showBottomButtons={true}
        content={
          <>
            <StyledResponsiveCardLayout>
              <CardsWrapper>
                {isIllinois && !primaryContact?.customerEmail && (
                  <StyledBanner
                    bannerType={BannerTypes.WarningLight}
                    primaryAction={{
                      title: "Enter email",
                      action: () => setShowTitleSlideout(true),
                    }}
                    show={true}
                    showClose={false}
                  >
                    Email is required before saving an offer or the Illinois IPA
                    rebate process will fail in Checkout
                  </StyledBanner>
                )}
                {showContractReviewBanners && (
                  <>
                    <ContractReviewBanners
                      messages={contractReviewMessages}
                      category="utility_bill_check"
                      filter={isRateScheduleMismatchMessage}
                      actionLink={{
                        text: "Update rate",
                        onClick: () => setShowUsageSlideout(true),
                      }}
                    />
                    <ContractReviewBanners
                      messages={contractReviewMessages}
                      category="estimation_method_check"
                      filter={is12MonthActualUsageMethodMessage}
                      actionLink={{
                        text: "Update usage",
                        onClick: () => setShowUsageSlideout(true),
                      }}
                    />
                  </>
                )}
                {utilityBillPastDueBalanceFlowIsEnabled &&
                  hasUploadedUtilityBillFileWithPastDueBalance && (
                    <UtilityBillPastDueBalanceBanner
                      utilityCompany={utilityCompany ?? ""}
                      utilityPastDueBalance={utilityPastDueBalance ?? 0}
                    />
                  )}
                <Row>
                  <TitleTask
                    loading={isContactsLoading}
                    signingNames={[
                      `${primaryContact?.customerFirstName} ${primaryContact?.customerLastName}`,
                    ]}
                    onOpen={() => setShowTitleSlideout(true)}
                    titleNames={titleNames}
                  />
                  <ConfirmHomeInfoTask
                    loading={isProspectLoading || isRefreshingHomeInfo}
                    hasAdditionalSystem={hasAdditionalSystem}
                    utilityCompany={utilityCompany}
                    homeType={prospect?.homeType}
                    languagePreference={prospect?.preferredLanguage}
                    onOpen={() => setShowHomeInfoSlideout(true)}
                  />
                </Row>
                <Row>
                  <UtilityTask
                    usage={usageData}
                    onOpen={() => setShowUsageSlideout(true)}
                    prospectId={prospectId || ""}
                    intervalDataStatus={intervalDataStatus}
                  />
                  <SiteModelTask
                    prospectId={prospectId || ""}
                    openModal={() => setOpenSiteModelModal(true)}
                  />
                </Row>
                <Typography
                  fontWeight="bold"
                  size={18}
                  color={tokens.BRAND_HERO_BLUE}
                >
                  Customer eligibility and optional tasks
                </Typography>
                <Row>
                  {!userProfile || !prospect ? (
                    <ConfirmInfoOptionalTask
                      title="Vivint Smart Home"
                      loading={true}
                    />
                  ) : (
                    <VivintOptionalTaskCard
                      leadSource={prospect?.leadSource}
                      isPartner={userProfile.isPartner}
                    />
                  )}
                  {(isHqsEligible === undefined || isHqsEligible) &&
                    !hqsError && (
                      <ConfirmInfoOptionalTask
                        title="Fastlane Survey"
                        body={{
                          type: "success",
                          text: "Fastlane review eligible",
                        }}
                        onLaunch={
                          isHqsEligible
                            ? () => window.open(hqsUrl, "_blank")
                            : undefined
                        }
                        onLaunchText="Launch"
                        loading={isHqsEligible === undefined}
                        testId="fastlane-survey-launch"
                      />
                    )}
                  <ConfirmInfoOptionalTask
                    title="Customer qualification"
                    loading={!primaryContact}
                    body={[
                      {
                        type: "info",
                        text: "Soft credit check to qualify for monthly product",
                      },
                      isCreditCheckPassed
                        ? {
                            type: "success",
                            text: "Credit Approved",
                          }
                        : undefined,
                    ]}
                    onLaunch={
                      !isCreditCheckPassed
                        ? () => {
                            setOpenQualifyModal(true);
                          }
                        : undefined
                    }
                  />
                  {!prospect ||
                    (LOAD_CALCULATOR_UTILITIES.includes(
                      prospect?.utilityCompany || ""
                    ) && (
                      <ConfirmInfoOptionalTask
                        title="Utility load calculator"
                        onLaunch={() =>
                          window.open(UTILITY_LOAD_CALCULATOR_URL, "_blank")
                        }
                        body={{
                          type: "info",
                          text: `For customers with less than 12 months kWh, ${prospect?.utilityCompany} requires this load calculator`,
                        }}
                        tooltip="The usage calculator helps estimate future usage for most utilities. However, this utility requires its own specific calculator, which you'll need to use separately."
                        loading={!prospect}
                      />
                    ))}
                  <ConfirmInfoOptionalTask
                    title="Usage calculator"
                    body={{
                      type: "warning",
                      text: "Estimate future electricity usage",
                    }}
                    onLaunch={() => {
                      window.open(usageCalculatorUrl, "_blank");
                    }}
                  />
                </Row>
              </CardsWrapper>
            </StyledResponsiveCardLayout>
            <QualifyCustomerModal
              open={isQualifyModalOpen}
              onClose={() => setOpenQualifyModal(false)}
            />

            <SiteModelModal
              open={isSiteModelModalOpen}
              prospectId={prospectId}
              onClose={() => setOpenSiteModelModal(false)}
            />
            {showHomeInfoSlideout && (
              <CheckoutCustomerInfoSlideout
                onClose={() => setShowHomeInfoSlideout(false)}
                prospectId={prospectId}
                onSuccess={handleHomeInfoSaveSuccess}
              />
            )}
            {showUsageSlideout && (
              <UsageSlideout
                utilityBillPastDueBalanceFlowIsEnabled={
                  utilityBillPastDueBalanceFlowIsEnabled
                }
                hasUploadedUtilityBillFileWithPastDueBalance={
                  hasUploadedUtilityBillFileWithPastDueBalance
                }
                utilityPastDueBalance={utilityPastDueBalance ?? 0}
                utilityCompany={utilityCompany ?? ""}
                onClose={(isDismissed) => {
                  if (isDismissed) {
                    setShowContractReviewBanners(true);
                  }
                  setShowUsageSlideout(false);
                }}
                onSave={() => setShowContractReviewBanners(false)}
                onGetBillOnline={
                  isUtilityAPICompany ? onGetBillOnline : undefined
                }
                intervalDataInitialLoading={intervalDataInitialLoading}
                intervalDataStatus={intervalDataStatus}
                isSunrunManaged={isSRM}
                contractReviewMessages={contractReviewMessages}
              />
            )}
            {showTitleSlideout && (
              <TitleInfoSlideout
                onClose={() => setShowTitleSlideout(false)}
                titleNames={titleNames}
              />
            )}
            {/**
           * (TODO) Add this back once we can put Usage Calculator into an iFrame
            {showUsageCalculatorSlideout && (
              <UsageCalculatorSlideout
                src={usageCalculatorUrl}
                onClose={() => setShowUsageCalculatorSlideout(false)}
              />
            )}
          **/}
          </>
        }
      />
      {showGetBillOnlineIframe && (
        <UtilityConnectionContainer
          src={url || ""}
          isLoading={loading}
          onClose={() => {
            setShowGetBillOnlineIframe(false);
            startPolling();
          }}
          onError={() => setShowGetBillOnlineIframe(false)}
          returnButtonText={"Return to confirm info"}
        />
      )}
      {showGetBillOnlineWarningModal && (
        <GetBillOnlineWarningModal
          type="info"
          onClose={() => setShowGetBillOnlineWarningModal(false)}
          title="Get bill online will not update usage"
          customModalWidth={880}
          testId="get-bill-online-warning-modal"
          closeButtonTestId="get-bill-online-warning-modal-close-button"
          secondaryButton={{
            text: "Cancel",
            onClick: () => setShowGetBillOnlineWarningModal(false),
            testId: "get-bill-online-warning-modal-secondary-button",
          }}
          primaryButton={{
            text: "Continue",
            onClick: () => {
              setShowGetBillOnlineWarningModal(false);
              getUrl(prospectId || "");
              setShowGetBillOnlineIframe(true);
            },
            testId: "get-bill-online-warning-modal-primary-button",
          }}
        >
          <Typography color={tokens.TINTS_OFF_WHITE_20} size={20}>
            Since you already have kWh entered and/or offers generated, Get bill
            online will not update kWh. To protect your previous work, Get bill
            online will only update fields like meter/account number and add
            utility bill files.
          </Typography>
          <br />
          <Typography color={tokens.TINTS_OFF_WHITE_20} size={20}>
            If you want Get bill online to update kWh usage you will first need
            to delete existing kWh entries and/or existing offers.
          </Typography>
        </GetBillOnlineWarningModal>
      )}
    </>
  );
};

const StyledResponsiveCardLayout = styled(ResponsiveCardLayout)`
  max-width: 1256px;
  min-width: 512px;
  margin: 0 auto;
  @media (max-width: 1100px) {
    flex-direction: column;
  }
  margin-bottom: 40px;
`;

const HeaderContentLayoutStyled = styled(HeaderContentLayout)`
  max-width: 1200px;
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const StyledBanner = styled(Banner)`
  border: 0px;
  box-shadow: 0px 0px 16px 0px #1f264714;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: stretch;
  flex-wrap: wrap;
  & > * {
    flex: 1;
  }
`;

const GetBillOnlineWarningModal = styled(Modal)`
  .modal-content-container {
    padding-top: 0;
  }
`;

export { ConfirmInfoPage };
